/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2025 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { assetsBatchesApiUrls } from '@/configs/api-urls/assets-batches.api-urls'
import { apiUriBase } from '@/configs/urls'
import { handleThrowError } from '@/utils/fetch'
import { setOffset } from '@/utils/pagination'
import { setAuthorizationConfig } from '@/utils/services'
import { setUrlWithParams } from '@/utils/url'
import axios from 'axios'

const apiUrlBase = assetsBatchesApiUrls.base

export const registerAssetsBatch = async (formData, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken, 'multipart/form-data')

  return axios
    .post(
      apiUrlBase,
      formData,
      config
    )
    .then(res => res.data)
    .catch(err => { throw handleThrowError('registerAssetsBatch', err) })
}

export const updateAssetsBatch = async ({ id, formData }, getAccessToken) => {
  const contentType = formData.has
    ? 'multipart/form-data'
    : undefined
  const config = await setAuthorizationConfig(getAccessToken, contentType)

  return axios
    .put(
      `${apiUrlBase}/${id}`,
      formData,
      config
    )
    .then(res => res.data)
    .catch(err => { handleThrowError('updateAssetsBatch', err) })
}

export const startAssetsBatchProcessing = async (id, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(setUrlWithParams(`${apiUrlBase}/${id}/start-processing`), config)
    .then(res => res.data)
    .catch(err => { handleThrowError('startAssetsBatchProcessing', err) })
}

export const getAssetsBatches = async ({ searchParams = {} }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const pagination = {
    limit: 10,
    offset: setOffset(searchParams.page, 10)
  }
  const url = setUrlWithParams(`${apiUrlBase}`, { ...pagination, ...searchParams })

  return axios
    .get(url, config)
    .then(res => { console.log(res); return res.data })
    .catch(err => { handleThrowError('getAssetsBatches', err) })
}

export const getAssetsBatch = async ({ id }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(setUrlWithParams(`${apiUrlBase}/${id}`), config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAssetsBatch', err) })
}

export const getAssetsBatchDataError = async (id, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(setUrlWithParams(`${apiUriBase}/assets-data-errors/${id}`), config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAssetsBatchDataError', err) })
}

export const deleteAssetsBatchDataError = async (id, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .delete(setUrlWithParams(`${apiUriBase}/assets-data-errors/${id}`), config)
    .then(res => res.data)
    .catch(err => { handleThrowError('deleteAssetsBatchDataError', err) })
}

export const getAssetsBatchDataStageErrors = async ({ id, dataId }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(setUrlWithParams(`${apiUrlBase}/${id}/data/${dataId}/stage-errors`), config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAssetsBatchDataStageErrors', err) })
}

export const getAssetsBatchDataByBatch = async ({
  id,
  searchParams = {}
}, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const pagination = {
    limit: 10,
    offset: setOffset(searchParams.page, 10)
  }
  const url = setUrlWithParams(`${apiUrlBase}/${id}/data`, { ...pagination, ...searchParams })

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAssetsBatchDataByBatch', err) })
}

export const getAssetsBatchDataByBatchAsCsv = async ({
  id
}, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  const url = setUrlWithParams(`${apiUrlBase}/${id}/data/reports/csv`)

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAssetsBatchDataByBatchAsCsv', err) })
}

export const getAssetsBatchDataErrorByBatch = async ({
  id,
  searchParams = {}
}, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const pagination = {
    limit: 10,
    offset: setOffset(searchParams.page, 10)
  }
  const url = setUrlWithParams(`${apiUrlBase}/${id}/data-errors`, { ...pagination, ...searchParams })

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAssetsBatchDataErrorByBatch', err) })
}

export const deleteAssetsBatchData = async ({ id, dataId }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .delete(setUrlWithParams(`${apiUrlBase}/${id}/data/${dataId}`), config)
    .then(res => res.data)
    .catch(err => { handleThrowError('deleteAssetsBatchData', err) })
}

export const updateAssetsBatchData = async ({ dataId, data }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .put(
      `${apiUriBase}/assets-data/${dataId}`,
      data,
      config
    )
    .then(res => res.data)
    .catch(err => { handleThrowError('updateAssetsBatchData', err) })
}

export const fixAssetBatchDataError = async ({ dataId, data }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .put(
      `${apiUriBase}/assets-data-errors/${dataId}`,
      data,
      config
    )
    .then(res => res.data)
    .catch(err => { handleThrowError('fixAssetBatchDataError', err) })
}

export const retryBatchProcess = async (worksBatchId, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .get(
      `${apiUrlBase}/${worksBatchId}/retry-processing`,
      config
    )
    .then(res => res.data)
    .catch(err => { handleThrowError('retryBatchProcess', err) })
}
